// src/features/invoices/components/ZusatzleistungenView.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Check, X, Edit3, Trash2, Search } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { zusatzleistungenService } from '../../../services/zusatzleistungen/zusatzleistungenService';
import { Zusatzleistung, ZusatzleistungArt, ZusatzleistungFilter } from '../types';

const GERMAN_MONTHS: Record<number, string> = {
  1: 'Januar', 2: 'Februar', 3: 'März', 4: 'April', 5: 'Mai', 6: 'Juni',
  7: 'Juli', 8: 'August', 9: 'September', 10: 'Oktober', 11: 'November', 12: 'Dezember'
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
};

// Komma-Eingabe → Zahl (deutsche Notation)
const parseComma = (s: string): number => {
  const n = parseFloat(s.replace(',', '.'));
  return isNaN(n) ? NaN : n;
};

const numToComma = (n: number): string => String(n).replace('.', ',');

interface CustomerOption {
  id: string;
  company: string;
  mandantennummer: string | null;
}

export const ZusatzleistungenView: React.FC = () => {
  const now = new Date();

  // ---- Stammdaten für Kundensuche ----
  const [customers, setCustomers] = useState<CustomerOption[]>([]);

  // ---- Formular-State ----
  const [editingId, setEditingId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerLabel, setCustomerLabel] = useState('');
  const [search, setSearch] = useState('');
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [art, setArt] = useState<ZusatzleistungArt>('stunden');
  const [stundenInput, setStundenInput] = useState('');
  const [satzInput, setSatzInput] = useState('');
  const [betragInput, setBetragInput] = useState(''); // nur bei Festbetrag
  const [beschreibung, setBeschreibung] = useState('');
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [formMessage, setFormMessage] = useState<string | null>(null);

  // ---- Listen-State ----
  const [items, setItems] = useState<Zusatzleistung[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState<number>(now.getMonth() + 1); // 0 = alle Monate
  const [filterYear, setFilterYear] = useState<number>(now.getFullYear());
  const [filterStatus, setFilterStatus] = useState<'' | 'offen' | 'in_rechnung'>('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ---- Berechnete Werte ----
  const stundenNum = parseComma(stundenInput);
  const satzNum = parseComma(satzInput);
  const computedStundenBetrag = (!isNaN(stundenNum) && !isNaN(satzNum))
    ? Math.round(stundenNum * satzNum * 100) / 100
    : NaN;
  const festBetragNum = parseComma(betragInput);
  const effectiveBetrag = art === 'stunden' ? computedStundenBetrag : festBetragNum;
  const canSubmit = !!customerId
    && beschreibung.trim() !== ''
    && !isNaN(effectiveBetrag)
    && effectiveBetrag !== 0
    && (art !== 'stunden' || (!isNaN(stundenNum) && stundenNum > 0));

  // ---- Kunden laden (einmalig) ----
  useEffect(() => {
    const loadCustomers = async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company, mandantennummer')
        .eq('deleted', false)
        .order('company')
        .limit(100000);
      if (!error && data) setCustomers(data as CustomerOption[]);
    };
    loadCustomers();
  }, []);

  // ---- Liste laden ----
  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const filter: ZusatzleistungFilter = {};
      if (filterMonth) filter.month = filterMonth;
      if (filterYear) filter.year = filterYear;
      if (filterStatus) filter.status = filterStatus;
      const data = await zusatzleistungenService.list(filter);
      setItems(data);
    } finally {
      setListLoading(false);
    }
  }, [filterMonth, filterYear, filterStatus]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ---- Kundensuche ----
  const query = search.trim().toLowerCase();
  const matches = query === ''
    ? []
    : customers.filter(c =>
        (c.company || '').toLowerCase().includes(query) ||
        (c.mandantennummer || '').toLowerCase().includes(query)
      ).slice(0, 8);

  const selectCustomer = async (c: CustomerOption) => {
    setCustomerId(c.id);
    setCustomerLabel(`${c.company}${c.mandantennummer ? ` (${c.mandantennummer})` : ''}`);
    setSearch('');
    // Stundensatz des Kunden vorschlagen (überschreibbar). Bei Unterkunden liefert
    // der Service den Satz des Auftraggebers.
    const satz = await zusatzleistungenService.getCurrentStundensatz(c.id);
    if (satz) setSatzInput(numToComma(Number(satz.stundensatz)));
  };

  const resetForm = () => {
    setEditingId(null);
    setCustomerId(null);
    setCustomerLabel('');
    setSearch('');
    setArt('stunden');
    setStundenInput('');
    setSatzInput('');
    setBetragInput('');
    setBeschreibung('');
    setNotiz('');
    setFormMessage(null);
    setMonth(new Date().getMonth() + 1);
    setYear(new Date().getFullYear());
  };

  const handleSubmit = async () => {
    if (!canSubmit || !customerId) return;
    setSaving(true);
    setFormMessage(null);
    try {
      if (editingId) {
        const res = await zusatzleistungenService.update(editingId, {
          year,
          month,
          art,
          stunden: art === 'stunden' ? stundenNum : null,
          stundensatz: art === 'stunden' ? satzNum : null,
          betrag: effectiveBetrag,
          beschreibung: beschreibung.trim(),
          notiz: notiz.trim() || null
        });
        if (!res.success) {
          setFormMessage(res.error || 'Aktualisierung fehlgeschlagen');
          setSaving(false);
          return;
        }
      } else {
        const created = await zusatzleistungenService.create({
          customerId,
          year,
          month,
          art,
          stunden: art === 'stunden' ? stundenNum : null,
          stundensatz: art === 'stunden' ? satzNum : null,
          betrag: effectiveBetrag,
          beschreibung: beschreibung.trim(),
          notiz: notiz.trim() || null
        });
        if (!created) {
          setFormMessage('Zusatzleistung konnte nicht angelegt werden');
          setSaving(false);
          return;
        }
      }
      resetForm();
      await loadList();
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (z: Zusatzleistung) => {
    setEditingId(z.id);
    setCustomerId(z.customer_id);
    setCustomerLabel(z.customer ? `${z.customer.company}${z.customer.mandantennummer ? ` (${z.customer.mandantennummer})` : ''}` : '');
    setSearch('');
    setYear(z.year);
    setMonth(z.month);
    setArt(z.art);
    setStundenInput(z.stunden !== null ? numToComma(Number(z.stunden)) : '');
    setSatzInput(z.stundensatz !== null ? numToComma(Number(z.stundensatz)) : '');
    setBetragInput(z.art === 'festbetrag' ? numToComma(Number(z.betrag)) : '');
    setBeschreibung(z.beschreibung);
    setNotiz(z.notiz || '');
    setFormMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Diese Zusatzleistung wirklich löschen?')) return;
    setDeletingId(id);
    try {
      const res = await zusatzleistungenService.remove(id);
      if (!res.success) {
        alert(res.error || 'Löschen fehlgeschlagen');
        return;
      }
      if (editingId === id) resetForm();
      await loadList();
    } finally {
      setDeletingId(null);
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px',
    fontSize: '14px', color: '#111827', backgroundColor: 'white', outline: 'none',
    boxSizing: 'border-box', width: '100%'
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px', display: 'block'
  };

  return (
    <div>
      {/* ============================================ */}
      {/* ERFASSUNG                                    */}
      {/* ============================================ */}
      <div style={{
        backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
        padding: '20px', marginBottom: '24px'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 16px 0' }}>
          {editingId ? 'Zusatzleistung bearbeiten' : 'Zusatzleistung erfassen'}
        </h3>

        {/* Kunde */}
        <div style={{ marginBottom: '16px', position: 'relative' }}>
          <label style={labelStyle}>Kunde</label>
          {customerId && customerLabel ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 12px', border: '1px solid #BFDBFE', borderRadius: '6px',
              backgroundColor: '#EFF6FF'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#1E40AF' }}>{customerLabel}</span>
              {!editingId && (
                <button
                  onClick={() => { setCustomerId(null); setCustomerLabel(''); setSatzInput(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#1E40AF' }}
                  aria-label="Kunde ändern"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ) : (
            <>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Firma oder Mandantennummer suchen..."
                  style={{ ...inputStyle, paddingLeft: '32px' }}
                />
              </div>
              {matches.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                  marginTop: '4px', backgroundColor: 'white', border: '1px solid #E5E7EB',
                  borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden'
                }}>
                  {matches.map(c => (
                    <button
                      key={c.id}
                      onClick={() => selectCustomer(c)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                        background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#111827'
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F3F4F6'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                    >
                      {c.company}{c.mandantennummer ? <span style={{ color: '#6B7280' }}> · {c.mandantennummer}</span> : null}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Monat / Jahr / Art */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Leistungsmonat</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={inputStyle}>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                <option key={m} value={m}>{GERMAN_MONTHS[m]}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Jahr</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={inputStyle}>
              {[2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Art</label>
            <select value={art} onChange={(e) => setArt(e.target.value as ZusatzleistungArt)} style={inputStyle}>
              <option value="stunden">Stunden</option>
              <option value="festbetrag">Festbetrag / Gutschrift</option>
            </select>
          </div>
        </div>

        {/* Betrag-Eingabe je nach Art */}
        {art === 'stunden' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Stunden</label>
              <input
                inputMode="decimal"
                value={stundenInput}
                onChange={(e) => { const r = e.target.value; if (r === '' || /^[\d.,]*$/.test(r)) setStundenInput(r); }}
                placeholder="z.B. 0,5"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Stundensatz (€)</label>
              <input
                inputMode="decimal"
                value={satzInput}
                onChange={(e) => { const r = e.target.value; if (r === '' || /^[\d.,]*$/.test(r)) setSatzInput(r); }}
                placeholder="z.B. 45,00"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Betrag (netto)</label>
              <div style={{ ...inputStyle, backgroundColor: '#F9FAFB', color: isNaN(computedStundenBetrag) ? '#9CA3AF' : '#111827', display: 'flex', alignItems: 'center' }}>
                {isNaN(computedStundenBetrag) ? '–' : formatCurrency(computedStundenBetrag)}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Betrag (netto, negativ = Gutschrift)</label>
              <input
                inputMode="decimal"
                value={betragInput}
                onChange={(e) => { const r = e.target.value; if (r === '' || /^-?[\d.,]*$/.test(r)) setBetragInput(r); }}
                placeholder="z.B. 116,25 oder -14,00"
                style={inputStyle}
              />
            </div>
          </div>
        )}

        {/* Beschreibung / Notiz */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Beschreibung</label>
          <textarea
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
            placeholder="z.B. Arbeitsbescheinigung Mustermann"
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Notiz (optional)</label>
          <textarea
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="Interne Notiz"
            rows={1}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {formMessage && (
          <div style={{
            padding: '10px 12px', marginBottom: '16px', borderRadius: '6px',
            backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', fontSize: '13px'
          }}>
            {formMessage}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            style={{
              padding: '8px 16px',
              backgroundColor: (!canSubmit || saving) ? '#9CA3AF' : '#3B82F6',
              color: 'white', border: 'none', borderRadius: '6px',
              cursor: (!canSubmit || saving) ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            {editingId ? <Check size={16} /> : <Plus size={16} />}
            {saving ? 'Speichern...' : (editingId ? 'Änderungen speichern' : 'Zusatzleistung erfassen')}
          </button>
          {editingId && (
            <button
              onClick={resetForm}
              disabled={saving}
              style={{
                padding: '8px 16px', backgroundColor: '#F3F4F6', color: '#374151',
                border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '500'
              }}
            >
              Abbrechen
            </button>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* LISTE                                        */}
      {/* ============================================ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(Number(e.target.value))}
          style={{ ...inputStyle, width: 'auto' }}
        >
          <option value={0}>Alle Monate</option>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
            <option key={m} value={m}>{GERMAN_MONTHS[m]}</option>
          ))}
        </select>
        <select
          value={filterYear}
          onChange={(e) => setFilterYear(Number(e.target.value))}
          style={{ ...inputStyle, width: 'auto' }}
        >
          {[2025, 2026, 2027].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as '' | 'offen' | 'in_rechnung')}
          style={{ ...inputStyle, width: 'auto' }}
        >
          <option value="">Alle Status</option>
          <option value="offen">Offen</option>
          <option value="in_rechnung">In Rechnung</option>
        </select>
      </div>

      {listLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>Lade Zusatzleistungen...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
          <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '4px' }}>Keine Zusatzleistungen</div>
          <div style={{ fontSize: '14px' }}>Für den gewählten Filter sind keine Einträge vorhanden.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.map(z => {
            const offen = z.status === 'offen';
            return (
              <div key={z.id} style={{
                padding: '14px 16px', backgroundColor: 'white', borderRadius: '8px',
                border: '1px solid #E5E7EB', display: 'grid',
                gridTemplateColumns: '1fr auto auto auto', alignItems: 'center', gap: '16px'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {z.customer?.company || '—'}
                    {z.customer?.mandantennummer ? <span style={{ color: '#9CA3AF', fontWeight: '400' }}> · {z.customer.mandantennummer}</span> : null}
                  </div>
                  <div style={{ fontSize: '13px', color: '#374151', marginTop: '2px', whiteSpace: 'pre-wrap' }}>
                    {z.beschreibung}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                    {GERMAN_MONTHS[z.month]} {z.year}
                    {z.art === 'stunden' && z.stunden !== null
                      ? ` · ${numToComma(Number(z.stunden))} Std${z.stundensatz !== null ? ` à ${formatCurrency(Number(z.stundensatz))}` : ''}`
                      : ' · Festbetrag'}
                  </div>
                  {z.auftraggeber_company && (
                    <div style={{ fontSize: '12px', color: '#2563EB', marginTop: '2px' }}>
                      → berechnet an: {z.auftraggeber_company}
                    </div>
                  )}
                  {z.notiz && (
                    <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px', fontStyle: 'italic' }}>
                      {z.notiz}
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'right', minWidth: '90px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: Number(z.betrag) < 0 ? '#DC2626' : '#111827' }}>
                    {formatCurrency(Number(z.betrag))}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>netto</div>
                </div>

                <span style={{
                  padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                  backgroundColor: offen ? '#FFFBEB' : '#ECFDF5',
                  color: offen ? '#B45309' : '#059669',
                  border: `1px solid ${offen ? '#FDE68A' : '#A7F3D0'}`,
                  whiteSpace: 'nowrap'
                }}>
                  {offen ? 'Offen' : 'In Rechnung'}
                </span>

                <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end', minWidth: '56px' }}>
                  {offen ? (
                    <>
                      <button
                        onClick={() => startEdit(z)}
                        style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6' }}
                        aria-label="Bearbeiten"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(z.id)}
                        disabled={deletingId === z.id}
                        style={{ padding: '4px', background: 'none', border: 'none', cursor: deletingId === z.id ? 'not-allowed' : 'pointer', color: '#EF4444' }}
                        aria-label="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>gesperrt</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZusatzleistungenView;
